import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { featuredProductService, FeaturedProduct } from '../../services/featuredProductService';
import { productService } from '../../services/productService';
import { Product } from '../../types';
import { getProductImageUrl, hasProductImage } from '../../utils/imageUtils';
import AddFeaturedProductForm from '../../components/AddFeaturedProductForm';
import ProductImage from '../../components/common/ProductImage';

const FeaturedProductManagement: React.FC = () => {
    const { user } = useAuth();
    const [featuredProducts, setFeaturedProducts] = useState<FeaturedProduct[]>([]);
    const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load featured products
            const featuredResponse = await featuredProductService.getFeaturedProducts();
            console.log('✅ Featured products response:', featuredResponse);
            setFeaturedProducts(featuredResponse.featuredProducts);

            // Load all available products for selection (including unavailable ones for admin)
            const productsResponse = await productService.getProducts({ limit: 1000 }, true); // true = includeUnavailable, high limit for admin
            setAvailableProducts(productsResponse.products);
        } catch (error) {
            console.error('❌ Erreur lors du chargement des données:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveProduct = async (id: number, productName: string) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir retirer "${productName}" des produits en vedette ?`)) {
            return;
        }

        try {
            await featuredProductService.removeFeaturedProduct(id);
            await loadData();
            alert('Produit retiré des produits en vedette avec succès !');
        } catch (error: any) {
            alert(error.message || 'Erreur lors de la suppression');
        }
    };

    const handleToggleActive = async (id: number, currentStatus: boolean) => {
        try {
            await featuredProductService.updateFeaturedProduct(id, {
                isActive: !currentStatus
            });
            await loadData();
        } catch (error: any) {
            alert(error.message || 'Erreur lors de la mise à jour');
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleReorder = async (dragIndex: number, hoverIndex: number) => {
        // TODO: Implement drag and drop reordering functionality
        console.log('Reorder feature coming soon');
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    const getImageUrl = (product: FeaturedProduct) => {
        // Try S3 image first if we have a product code
        if (hasProductImage({ code: product.code })) {
            return getProductImageUrl(product.code);
        }
        // Fallback to existing photo_url
        return product.photo_url;
    };

    // Show loading state if still loading
    if (loading) {
        return (
            <div className="min-h-screen bg-eldai-gray-light flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-eldai-blue mx-auto mb-4"></div>
                    <p className="text-eldai-gray">Chargement des produits en vedette...</p>
                </div>
            </div>
        );
    }

    // Check admin access - but allow loading state
    if (!loading && user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-eldai-gray-light flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h1>
                    <p className="text-eldai-gray">Vous n'avez pas les permissions pour accéder à cette page.</p>
                    <Link to="/login" className="btn btn-primary mt-4 inline-block">
                        Se connecter
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-container">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <Link
                            to="/admin"
                            className="admin-nav-back"
                        >
                            <span className="admin-nav-back-icon">←</span> Retour au tableau de bord
                        </Link>
                    </div>
                    <div className="admin-header">
                        <div>
                            <h1 className="admin-title">
                                <span className="admin-title-icon">⭐</span> Gestion des produits en vedette
                            </h1>
                            <p className="admin-subtitle">
                                Gérez les produits affichés sur la page d'accueil ({featuredProducts.filter(fp => fp.is_active).length} actifs)
                            </p>
                        </div>
                    </div>
                </div>

                {/* Add New Featured Product */}
                <AddFeaturedProductForm
                    availableProducts={availableProducts.filter(product => {
                        const isAlreadyFeatured = featuredProducts.some(fp => fp.product_id === product.id && fp.is_active);
                        return !isAlreadyFeatured;
                    })}
                    onProductAdded={loadData}
                />

                {/* Featured Products List */}
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h2 className="admin-card-title">
                            Produits en vedette ({featuredProducts.length})
                        </h2>
                    </div>

                    {loading ? (
                        <div className="admin-loading">
                            <div className="admin-loading-spinner"></div>
                            <div className="admin-loading-text">Chargement...</div>
                        </div>
                    ) : featuredProducts.length > 0 ? (
                        <div className="admin-table-container">
                            <table className="admin-table">
                                <thead className="admin-table-header">
                                    <tr>
                                        <th>Ordre</th>
                                        <th>Image</th>
                                        <th>Produit</th>
                                        <th>Prix</th>
                                        <th>Statut</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {featuredProducts.map((featuredProduct, index) => {
                                        const imageUrl = getImageUrl(featuredProduct);

                                        return (
                                            <tr key={featuredProduct.id}>
                                                <td>
                                                    <span className="admin-badge admin-badge-info">
                                                        #{featuredProduct.display_order}
                                                    </span>
                                                </td>
                                                <td>
                                                    <ProductImage
                                                        product={featuredProduct}
                                                        size="medium"
                                                    />
                                                </td>
                                                <td>
                                                    <div>
                                                        <p className="font-semibold text-gray-900">{featuredProduct.designation}</p>
                                                        <p className="text-sm text-gray-500">Code: {featuredProduct.code}</p>
                                                        {featuredProduct.category_name && (
                                                            <p className="text-sm text-eldai-gray">Catégorie: {featuredProduct.category_name}</p>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="text-lg font-bold text-gray-900">
                                                        {formatPrice(featuredProduct.unit_price)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="flex flex-col space-y-1">
                                                        <span className={`admin-badge ${featuredProduct.is_active
                                                            ? 'admin-badge-success'
                                                            : 'admin-badge-danger'
                                                            }`}>
                                                            {featuredProduct.is_active ? 'Actif' : 'Inactif'}
                                                        </span>
                                                        <span className={`admin-badge ${featuredProduct.is_available
                                                            ? 'admin-badge-info'
                                                            : 'admin-badge-warning'
                                                            }`}>
                                                            {featuredProduct.is_available ? 'Disponible' : 'Indisponible'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => handleToggleActive(featuredProduct.id, featuredProduct.is_active)}
                                                            className={`admin-btn admin-btn-small ${featuredProduct.is_active ? 'admin-btn-warning' : 'admin-btn-success'}`}
                                                            title={featuredProduct.is_active ? 'Désactiver' : 'Activer'}
                                                        >
                                                            {featuredProduct.is_active ? '⏸️' : '▶️'}
                                                        </button>
                                                        <Link
                                                            to={`/product/${featuredProduct.product_id}`}
                                                            className="admin-btn admin-btn-small admin-btn-secondary"
                                                            title="Voir le produit"
                                                        >
                                                            👁️
                                                        </Link>
                                                        <button
                                                            onClick={() => handleRemoveProduct(featuredProduct.id, featuredProduct.designation)}
                                                            className="admin-btn admin-btn-small admin-btn-danger"
                                                            title="Retirer des produits en vedette"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-16 text-center">
                            <div className="text-7xl mb-4">⭐</div>
                            <h3 className="text-2xl font-bold text-eldai-blue mb-2">
                                Aucun produit en vedette
                            </h3>
                            <p className="text-eldai-gray mb-4 text-lg">
                                Ajoutez des produits pour qu'ils apparaissent sur la page d'accueil.
                            </p>
                        </div>
                    )}
                </div>

                {/* Instructions */}
                <div className="admin-instructions">
                    <h3 className="admin-instructions-title">📋 Instructions d'utilisation</h3>
                    <div className="admin-instructions-grid">
                        <div className="admin-instructions-section">
                            <h4>🎯 Gestion des produits en vedette</h4>
                            <ul>
                                <li>Ajoutez des produits pour les mettre en avant sur la page d'accueil</li>
                                <li>L'ordre d'affichage peut être modifié (fonctionnalité à venir)</li>
                                <li>Activez/désactivez les produits selon vos besoins</li>
                            </ul>
                        </div>
                        <div className="admin-instructions-section">
                            <h4>📸 Images et affichage</h4>
                            <ul>
                                <li>Les images S3 sont prioritaires si disponibles</li>
                                <li>Tous les produits peuvent être ajoutés (disponibles ou non)</li>
                                <li>Les produits inactifs n'apparaissent pas sur le site</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeaturedProductManagement;
